import { useState, useCallback, useEffect } from 'react';
import { useNotificationBar } from 'context/notificationContext';
import { SubscriptionWidgetConfig } from 'interfaces/widget-types';
import { useUploadFiles } from 'hooks/upload';
import { getImageSrc } from 'services/util';
import { Upload } from 'lucide-react';

interface Props {
    widget: {
        value: string;
        label: string;
        code: string;
    };
    initialData?: any; // API response has nested structure
    onSave: (payload: SubscriptionWidgetConfig | null) => void;
    onCancel?: () => void;
}

const SubscriptionWidget = ({ widget, initialData, onSave, onCancel }: Props) => {
    const notification = useNotificationBar();
    const uploadMutation = useUploadFiles();
    
    // Extract data from the nested structure
    const widgetData = initialData?.data || initialData;
    
    // Form state
    const [heading, setHeading] = useState(widgetData?.heading || '');
    const [subHeading, setSubHeading] = useState(widgetData?.sub_heading || '');
    const [ctaText, setCtaText] = useState(widgetData?.cta_text || '');
    const [strikethroughText, setStrikethroughText] = useState(widgetData?.strikethrough_text || '');
    const [link, setLink] = useState(widgetData?.link || '');
    const [footerNote, setFooterNote] = useState(widgetData?.footer_note || '');
    
    // Image states
    const [images, setImages] = useState<string[]>([]);
    const [imageFiles, setImageFiles] = useState<File[]>([]);
    const [imagePreviews, setImagePreviews] = useState<string[]>([]);

    // Initialize image previews
    useEffect(() => {
        const widgetImages = widgetData?.images || [];
        setImages(widgetImages);
        
        const previews = widgetImages.map(img => {
            if (img) {
                const preview = img.startsWith('http') ? img : getImageSrc(img);
                return preview;
            }
            return '';
        }).filter(Boolean);
        setImagePreviews(previews);
    }, [widgetData]);

    // Cleanup blob URLs
    useEffect(() => {
        return () => {
            imagePreviews.forEach(preview => {
                if (preview.startsWith('blob:')) {
                    URL.revokeObjectURL(preview);
                }
            });
        };
    }, [imagePreviews]);

    const handleImageUpload = useCallback((index: number, file: File | null) => {
        if (file) {
            // Validate file
            if (file.size > 5 * 1024 * 1024) {
                notification.push({
                    message: 'File size must be under 5MB',
                    type: 'error',
                });
                return;
            }
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
            if (!allowedTypes.includes(file.type)) {
                notification.push({
                    message: 'Only JPG, JPEG, PNG, WebP allowed',
                    type: 'error',
                });
                return;
            }

            const newImageFiles = [...imageFiles];
            newImageFiles[index] = file;
            setImageFiles(newImageFiles);

            // Create preview
            const previewUrl = URL.createObjectURL(file);
            const newPreviews = [...imagePreviews];
            newPreviews[index] = previewUrl;
            setImagePreviews(newPreviews);
        }
    }, [imageFiles, imagePreviews, notification]);


    const handleSave = async () => {
        if (!heading || !subHeading) {
            notification.push({
                message: 'Please fill all required fields',
                type: 'error',
            });
            return;
        }

        // Check for total images (existing + new uploads)
        const totalImages = images.length + imageFiles.length;
        if (totalImages < 3) {
            notification.push({
                message: 'Please upload all three images',
                type: 'error',
            });
            return;
        }

        // Upload new images
        const uploadedImages = [...images];
        for (let i = 0; i < imageFiles.length; i++) {
            if (imageFiles[i] instanceof File) {
                try {
                    const formData = new FormData();
                    formData.append('file', imageFiles[i]);
                    formData.append('customPath', 'widgets/subscription');

                    const res = await uploadMutation.mutateAsync({
                        data: formData,
                    });
                    uploadedImages[i] = res?.data?.data?.key || '';
                } catch (error) {
                    notification.push({
                        message: 'Failed to upload image',
                        type: 'error',
                    });
                    return;
                }
            }
        }

        const data: any = {
            heading,
            sub_heading: subHeading,
            // cta_text: ctaText,
            // strikethrough_text: strikethroughText,
            // link: link || null,
            // footer_note: footerNote,
            images: uploadedImages,
            widget_code: widget.code,
            widget_type: Number(widget.value),
            view_all: false,
            source_id: "",
            source_type: "MANUAL",
            widget_icon: "",
            content_rule: "",
            content_type: "STATIC",
        };

        const finalData: any = {
            name:widget.label,
            widget_code: widget.code,
            widget_type: Number(widget.value),
            data
        };

        onSave(finalData);
    };

    const handleCancel = () => {
        if (initialData) {
            onSave(initialData);
        } else {
            onSave(null);
        }
    };

    return (
        <div className="space-y-6">

            {/* Content Configuration */}
            <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Content Configuration</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
                    <div className='flex flex-col gap-1'>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Heading *</label>
                        <input
                            value={heading}
                            onChange={(e) => setHeading(e.target.value)}
                            className="w-full px-3 py-2 text-sm border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:border-slate-600 dark:text-white dark:placeholder-gray-500"
                            placeholder="Enter heading"
                            required
                        />
                    </div>

                    <div className='flex flex-col gap-1'>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Sub Heading *</label>
                        <input
                            value={subHeading}
                            onChange={(e) => setSubHeading(e.target.value)}
                            className="w-full px-3 py-2 text-sm border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:border-slate-600 dark:text-white dark:placeholder-gray-500"
                            placeholder="Enter sub heading"
                            required
                        />
                    </div>

                    {/* <div className='flex flex-col gap-1'>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">CTA Text *</label>
                        <input
                            value={ctaText}
                            onChange={(e) => setCtaText(e.target.value)}
                            className="w-full px-3 py-2 text-sm border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:border-slate-600 dark:text-white dark:placeholder-gray-500"
                            placeholder="Enter CTA text"
                            required
                        />
                    </div>

                    <div className='flex flex-col gap-1'>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Strikethrough Text *</label>
                        <input
                            value={strikethroughText}
                            onChange={(e) => setStrikethroughText(e.target.value)}
                            className="w-full px-3 py-2 text-sm border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:border-slate-600 dark:text-white dark:placeholder-gray-500"
                            placeholder="Enter strikethrough text"
                            required
                        />
                    </div>

                    <div className='flex flex-col gap-1'>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Link</label>
                        <input
                            type="url"
                            value={link}
                            onChange={(e) => setLink(e.target.value)}
                            className="w-full px-3 py-2 text-sm border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:border-slate-600 dark:text-white dark:placeholder-gray-500"
                            placeholder="Enter link URL"
                        />
                    </div>

                    <div className='flex flex-col gap-1'>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Footer Note *</label>
                        <input
                            value={footerNote}
                            onChange={(e) => setFooterNote(e.target.value)}
                            className="w-full px-3 py-2 text-sm border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:border-slate-600 dark:text-white dark:placeholder-gray-500"
                            placeholder="Enter footer note"
                            required
                        />
                    </div> */}
                </div>
            </div>

            {/* Images */}
            <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Images *</h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl">
                    {[0, 1, 2].map((index) => (
                        <div key={index} className='flex flex-col gap-1'>
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Image {index + 1}
                            </label>
                            <div className="border-2 border-dashed rounded-lg p-3 hover:border-blue-400 transition">
                                <input
                                    id={`image-${index}`}
                                    type="file"
                                    accept="image/jpeg,image/jpg,image/png,image/webp"
                                    className="hidden"
                                    onChange={(e) => handleImageUpload(index, e.target.files?.[0] || null)}
                                />
                                <label htmlFor={`image-${index}`} className="flex flex-col items-center justify-center cursor-pointer">
                                    {imagePreviews[index] ? (
                                        <div className="relative w-full">
                                            <img
                                                src={imagePreviews[index]}
                                                alt={`Image ${index + 1} preview`}
                                                className="w-full h-32 object-cover rounded border"
                                            />
                                        </div>
                                    ) : (
                                        <>
                                            <Upload
                                                className="text-gray-400 mb-2"
                                                size={25}
                                            />
                                            <p className="text-sm text-gray-500">
                                                Click to upload image
                                            </p>
                                            <p className="text-xs text-gray-400">
                                                JPG / PNG / WebP · Max 5MB
                                            </p>
                                        </>
                                    )}
                                </label>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-6 mt-6 border-t border-gray-200 dark:border-slate-600">
                <button
                    onClick={handleCancel}
                    className="px-6 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                >
                    Cancel
                </button>
                <button
                    onClick={handleSave}
                    className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                    Save
                </button>
            </div>
        </div>
    );
};

export default SubscriptionWidget;
